'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ObservationSubmissionPanel from './ObservationSubmissionPanel';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface EcosystemZone {
  id: string;
  name: string;
  arabic: string;
  biome: string;
  icon: string;
  color: string;
  description: string;
  species: SpeciesEntry[];
  nameOfAllah: string;
  ayatRef: string;
}

interface SpeciesEntry {
  id: string;
  name: string;
  type: 'plant' | 'animal' | 'fungi' | 'water' | 'soil' | 'mineral';
  description: string;
  nameOfAllah: string;
  reflectionPrompt: string;
  ayatRef: string;
  icon: string;
}

interface Observation {
  id: string;
  zoneId: string;
  speciesId: string;
  speciesName: string;
  reflection: string;
  nameOfAllah: string;
  timestamp: string;
  status: 'pending' | 'approved' | 'community';
}

// ─── ECOSYSTEM DATA ───────────────────────────────────────────────────────────

const ECOSYSTEM_ZONES: EcosystemZone[] = [
  {
    id: 'urban-forest',
    name: 'Urban Forest',
    arabic: 'الْغَابَة الْحَضَرِيَّة',
    biome: 'Forest',
    icon: '🌳',
    color: '#2d7a47',
    description: 'City parks, street trees, community gardens, and green corridors — the living network woven through the built environment.',
    nameOfAllah: 'Al-Hayy',
    ayatRef: '2:255',
    species: [
      { id: 'oak', name: 'Oak Tree', type: 'plant', description: 'Long-lived keystone species supporting hundreds of other species. Its acorns feed squirrels, deer, and birds.', nameOfAllah: 'Al-Muhyi', reflectionPrompt: 'How does the oak\'s patient growth over decades reflect the quality of Al-Muhyi — The Giver of Life?', ayatRef: '6:99', icon: '🌳' },
      { id: 'mycorrhizal', name: 'Mycorrhizal Network', type: 'fungi', description: 'Underground fungal networks connecting tree roots, enabling nutrient sharing across the forest.', nameOfAllah: 'Al-Latif', reflectionPrompt: 'The hidden network beneath the soil connects trees invisibly. How does this reflect Al-Latif — The Subtle?', ayatRef: '31:16', icon: '🍄' },
      { id: 'sparrow', name: 'House Sparrow', type: 'animal', description: 'Urban-adapted bird that nests in building crevices and feeds on seeds and insects.', nameOfAllah: 'Ar-Razzaq', reflectionPrompt: 'The sparrow finds provision in the city without planting or storing. What does this reveal about Ar-Razzaq?', ayatRef: '67:19', icon: '🐦' },
      { id: 'dandelion', name: 'Dandelion', type: 'plant', description: 'Pioneer plant that breaks through concrete, fixes nitrogen, and provides early spring nectar for pollinators.', nameOfAllah: 'Al-Bari', reflectionPrompt: 'The dandelion grows through cracks in concrete. How does this reflect Al-Bari — The Evolver?', ayatRef: '39:21', icon: '🌼' },
    ],
  },
  {
    id: 'watershed',
    name: 'Watershed',
    arabic: 'حَوْض الْمَاء',
    biome: 'Riparian',
    icon: '💧',
    color: '#2d8a87',
    description: 'Rivers, streams, wetlands, and the land that drains into them — the water system that sustains all life in a region.',
    nameOfAllah: 'Al-Hayy',
    ayatRef: '21:30',
    species: [
      { id: 'river', name: 'River Current', type: 'water', description: 'Moving water that carves landscapes, transports nutrients, and sustains riparian ecosystems.', nameOfAllah: 'Al-Qadir', reflectionPrompt: 'Water flows according to the measure decreed for it. How does this reflect Al-Qadir — The All-Powerful?', ayatRef: '13:17', icon: '🌊' },
      { id: 'cattail', name: 'Cattail', type: 'plant', description: 'Wetland plant that filters water, provides habitat, and stabilizes shorelines.', nameOfAllah: 'Al-Musawwir', reflectionPrompt: 'The cattail\'s form is perfectly suited to its function. How does this reflect Al-Musawwir — The Fashioner?', ayatRef: '40:64', icon: '🌾' },
      { id: 'heron', name: 'Great Blue Heron', type: 'animal', description: 'Patient hunter that stands motionless in shallow water, striking with precision.', nameOfAllah: 'Al-Basir', reflectionPrompt: 'The heron\'s stillness and precision in hunting. How does this reflect Al-Basir — The All-Seeing?', ayatRef: '67:19', icon: '🦢' },
    ],
  },
  {
    id: 'community-garden',
    name: 'Community Garden',
    arabic: 'الْحَدِيقَة الْمُجْتَمَعِيَّة',
    biome: 'Cultivated',
    icon: '🌱',
    color: '#c9a84c',
    description: 'Shared growing spaces where community members cultivate food, relationships, and connection to the earth.',
    nameOfAllah: 'Ar-Razzaq',
    ayatRef: '6:99',
    species: [
      { id: 'kale', name: 'Kale', type: 'plant', description: 'Nutrient-dense leafy green that grows through winter, providing food security in cold months.', nameOfAllah: 'Ar-Razzaq', reflectionPrompt: 'Kale grows through frost and cold. How does its persistence reflect Ar-Razzaq — The Ever-Provider?', ayatRef: '2:22', icon: '🥬' },
      { id: 'bee', name: 'Honey Bee', type: 'animal', description: 'Essential pollinator that enables the reproduction of 80% of flowering plants.', nameOfAllah: 'Al-Wali', reflectionPrompt: 'The bee\'s role in sustaining plant life is invisible to most. How does this reflect Al-Wali — The Protecting Friend?', ayatRef: '16:68', icon: '🐝' },
      { id: 'compost', name: 'Compost', type: 'soil', description: 'Decomposed organic matter that returns nutrients to the soil, completing the cycle of life.', nameOfAllah: 'Al-Badi', reflectionPrompt: 'Decay becomes nourishment. How does compost reflect Al-Badi — The Originator who creates from nothing?', ayatRef: '56:63', icon: '🌿' },
    ],
  },
  {
    id: 'urban-sky',
    name: 'Urban Sky',
    arabic: 'السَّمَاء الْحَضَرِيَّة',
    biome: 'Atmospheric',
    icon: '☁️',
    color: '#8a8ab8',
    description: 'The atmosphere above the city — clouds, wind, rain, stars, and the light that sustains all photosynthesis.',
    nameOfAllah: 'Al-Nur',
    ayatRef: '24:35',
    species: [
      { id: 'cloud', name: 'Cumulus Cloud', type: 'water', description: 'Water vapor condensed around dust particles, carrying rain from ocean to land.', nameOfAllah: 'Al-Qadir', reflectionPrompt: 'Clouds carry water across continents without vessels. How does this reflect Al-Qadir — The All-Powerful?', ayatRef: '24:43', icon: '☁️' },
      { id: 'sunlight', name: 'Sunlight', type: 'mineral', description: 'Electromagnetic radiation that drives photosynthesis, warms the earth, and enables vision.', nameOfAllah: 'Al-Nur', reflectionPrompt: 'Light enables all sight and all growth. How does sunlight reflect Al-Nur — The Light?', ayatRef: '24:35', icon: '☀️' },
      { id: 'wind', name: 'Wind', type: 'water', description: 'Moving air that pollinates plants, disperses seeds, and regulates temperature.', nameOfAllah: 'Ar-Rahman', reflectionPrompt: 'Wind is invisible yet its effects are everywhere. How does this reflect Ar-Rahman — The Most Compassionate?', ayatRef: '25:48', icon: '🌬️' },
    ],
  },
];

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function EcosystemJourneyPage() {
  const [activeZone, setActiveZone] = useState<EcosystemZone | null>(null);
  const [activeSpecies, setActiveSpecies] = useState<SpeciesEntry | null>(null);
  const [reflection, setReflection] = useState('');
  const [observations, setObservations] = useState<Observation[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'explore' | 'observe' | 'community'>('explore');

  const handleSelectZone = useCallback((zone: EcosystemZone) => {
    setActiveZone(zone);
    setActiveSpecies(null);
    setReflection('');
    setSubmitSuccess(false);
  }, []);

  const handleSelectSpecies = useCallback((species: SpeciesEntry) => {
    setActiveSpecies(species);
    setReflection('');
    setSubmitSuccess(false);
    setActiveTab('observe');
  }, []);

  const handleSubmitObservation = useCallback(() => {
    if (!activeZone || !activeSpecies || !reflection.trim()) return;
    setSubmitting(true);
    setTimeout(() => {
      const obs: Observation = {
        id: `obs-${Date.now()}`,
        zoneId: activeZone.id,
        speciesId: activeSpecies.id,
        speciesName: activeSpecies.name,
        reflection: reflection.trim(),
        nameOfAllah: activeSpecies.nameOfAllah,
        timestamp: new Date().toISOString(),
        status: 'pending',
      };
      setObservations(prev => [obs, ...prev]);
      setSubmitting(false);
      setSubmitSuccess(true);
      setReflection('');
      setTimeout(() => setSubmitSuccess(false), 4000);
    }, 1200);
  }, [activeZone, activeSpecies, reflection]);

  return (
    <div className="min-h-screen" style={{ background: '#060d12' }}>
      <Header />

      {/* Hero */}
      <section style={{ background: '#060d12', borderBottom: '1px solid rgba(201,168,76,0.1)', padding: '64px 24px 48px' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 12 }}>
            رِحْلَة النِّظَام الْبِيئِي · ECOSYSTEM JOURNEY
          </p>
          <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(18px, 4vw, 32px)', color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 16 }}>
            OPEN WORLD JOURNEY
          </h1>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(212,197,169,0.8)', lineHeight: 1.8, marginBottom: 12 }}>
            "Do you not see that Allah sends down rain from the sky and makes it flow as springs in the earth; then He produces with it crops of different colors..."
          </p>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.4)', marginBottom: 20 }}>Quran 39:21</p>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 15, lineHeight: 1.8, maxWidth: 560, margin: '0 auto' }}>
            Explore local ecosystems, identify species and resources, document reflections linked to Names of Allah, and submit observations for community approval.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-12">

        {/* Tab navigation */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 32, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {([
            { id: 'explore', label: 'Explore Zones', icon: '◎' },
            { id: 'observe', label: 'Document Observation', icon: '⬡' },
            { id: 'community', label: `Community (${observations.length})`, icon: '✦' },
          ] as const).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '12px 20px', cursor: 'pointer', background: 'transparent',
              border: 'none', borderBottom: `2px solid ${activeTab === tab.id ? '#c9a84c' : 'transparent'}`,
              color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              transition: 'all 0.2s',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* EXPLORE TAB */}
        {activeTab === 'explore' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {ECOSYSTEM_ZONES.map(zone => (
                <div
                  key={zone.id}
                  onClick={() => handleSelectZone(zone)}
                  style={{
                    borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                    border: `1px solid ${activeZone?.id === zone.id ? zone.color + '60' : 'rgba(255,255,255,0.06)'}`,
                    borderTop: `3px solid ${zone.color}`,
                    background: activeZone?.id === zone.id ? `${zone.color}08` : 'rgba(6,13,18,0.7)',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 24 }}>{zone.icon}</span>
                      <div>
                        <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${zone.color}80`, direction: 'rtl' }}>{zone.arabic}</p>
                        <h3 style={{ fontFamily: 'monospace', fontSize: 13, color: zone.color, letterSpacing: '.06em', textTransform: 'uppercase' }}>{zone.name}</h3>
                      </div>
                      <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 20, background: `${zone.color}10`, border: `1px solid ${zone.color}25`, color: zone.color }}>
                        {zone.biome}
                      </span>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>{zone.description}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                        ✦ {zone.nameOfAllah}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{zone.species.length} species</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Zone species detail */}
            {activeZone && (
              <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${activeZone.color}25` }}>
                <div style={{ padding: '16px 20px', background: `${activeZone.color}08`, borderBottom: `1px solid ${activeZone.color}15` }}>
                  <h3 style={{ fontFamily: 'monospace', fontSize: 12, color: activeZone.color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {activeZone.icon} {activeZone.name} — Species & Resources
                  </h3>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>Select a species to document an observation</p>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeZone.species.map(species => (
                      <div
                        key={species.id}
                        onClick={() => handleSelectSpecies(species)}
                        style={{
                          padding: '14px 16px', borderRadius: 8, cursor: 'pointer',
                          background: activeSpecies?.id === species.id ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${activeSpecies?.id === species.id ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)'}`,
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 18 }}>{species.icon}</span>
                          <div>
                            <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#d4c5a9', letterSpacing: '.04em' }}>{species.name}</p>
                            <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '1px 6px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                              {species.nameOfAllah}
                            </span>
                          </div>
                        </div>
                        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5 }}>{species.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* OBSERVE TAB */}
        {activeTab === 'observe' && (
          <div className="max-w-2xl mx-auto">
            {!activeSpecies ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', marginBottom: 12 }}>
                  SELECT A SPECIES FROM THE EXPLORE TAB
                </p>
                <button onClick={() => setActiveTab('explore')} style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
                  background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c',
                }}>
                  ← Explore Zones
                </button>
              </div>
            ) : (
              <div>
                {/* Species header */}
                <div style={{ padding: '16px 20px', borderRadius: 10, background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(201,168,76,0.15)', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 28 }}>{activeSpecies.icon}</span>
                    <div>
                      <h3 style={{ fontFamily: 'monospace', fontSize: 14, color: '#d4c5a9', letterSpacing: '.04em' }}>{activeSpecies.name}</h3>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                        ✦ {activeSpecies.nameOfAllah}
                      </span>
                    </div>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6, marginBottom: 10 }}>{activeSpecies.description}</p>
                  <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>REFLECTION PROMPT</p>
                    <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#d4c5a9', lineHeight: 1.7, fontStyle: 'italic' }}>{activeSpecies.reflectionPrompt}</p>
                  </div>
                  <div style={{ marginTop: 10, padding: '8px 14px', borderRadius: 6, background: 'rgba(138,138,184,0.04)', border: '1px solid rgba(138,138,184,0.1)' }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(138,138,184,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>QURANIC REFERENCE · {activeSpecies.ayatRef}</p>
                  </div>
                </div>

                {/* Reflection input */}
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>
                    YOUR REFLECTION
                  </p>
                  <textarea
                    value={reflection}
                    onChange={e => setReflection(e.target.value)}
                    placeholder="Document what you observe and how it connects to the Name of Allah..."
                    rows={5}
                    style={{
                      width: '100%', padding: '14px 16px', borderRadius: 8, resize: 'vertical',
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.15)',
                      color: '#d4c5a9', fontFamily: "'Amiri', serif", fontSize: 14, lineHeight: 1.7,
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)', marginTop: 4 }}>
                    {reflection.length} characters · Minimum 50 characters to submit
                  </p>
                </div>

                {submitSuccess && (
                  <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', marginBottom: 16 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#2d7a47' }}>
                      ✓ Observation submitted for community review. Alhamdulilah.
                    </p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleSubmitObservation}
                    disabled={submitting || reflection.length < 50}
                    style={{
                      flex: 1, fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
                      padding: '12px 20px', borderRadius: 8, cursor: reflection.length >= 50 ? 'pointer' : 'not-allowed',
                      background: reflection.length >= 50 ? 'rgba(45,122,71,0.12)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${reflection.length >= 50 ? 'rgba(45,122,71,0.4)' : 'rgba(255,255,255,0.06)'}`,
                      color: reflection.length >= 50 ? '#2d7a47' : 'rgba(212,197,169,0.25)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {submitting ? '⟳ Submitting...' : '◈ Submit for Community Approval'}
                  </button>
                  <button onClick={() => setActiveTab('explore')} style={{
                    fontFamily: 'monospace', fontSize: 10, padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)',
                  }}>
                    ← Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* COMMUNITY TAB */}
        {activeTab === 'community' && (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 16 }}>
              COMMUNITY OBSERVATIONS
            </p>

            {/* Observation Submission Panel */}
            <div style={{ marginBottom: 32 }}>
              <ObservationSubmissionPanel />
            </div>

            {observations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12 }}>
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 13 }}>No observations yet. Explore a zone and document your first observation.</p>
                <button onClick={() => setActiveTab('explore')} style={{
                  marginTop: 16, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '8px 16px', borderRadius: 20, cursor: 'pointer',
                  background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c',
                }}>
                  Start Exploring →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {observations.map(obs => (
                  <div key={obs.id} style={{ padding: '16px 18px', borderRadius: 8, background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,122,71,0.15)', borderLeft: '3px solid #2d7a47' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 20, background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', color: '#2d7a47' }}>
                        {obs.status === 'pending' ? '⟳ Pending Review' : '✓ Approved'}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                        ✦ {obs.nameOfAllah}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>
                        {obs.speciesName}
                      </span>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>{obs.reflection}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div style={{ marginTop: 48, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
          <Link href="/creation-lens" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 20, border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', textDecoration: 'none' }}>
            Creation Lens →
          </Link>
          <Link href="/inner-reality-lens" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 20, border: '1px solid rgba(45,138,135,0.25)', color: '#2d8a87', textDecoration: 'none' }}>
            IRL Lens →
          </Link>
          <Link href="/asbab-quest" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: 20, border: '1px solid rgba(45,122,71,0.25)', color: '#2d7a47', textDecoration: 'none' }}>
            Asbab Quest →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
